import { useCallback, useEffect, useState } from "react";
import { Activity } from "lucide-react";
import { toast } from "sonner";
import { adminService } from "@/services/adminService";
import { errorMessage } from "@/services/apiClient";
import { SecurityTotals } from "@/components/security/SecurityParts";
import { BlockedIpsTab, SecurityEventsTab } from "@/components/security/SecurityTabs";

export const SecurityEventsPanel = () => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("events");
  const [manual, setManual] = useState({ ip: "", note: "" });

  const load = useCallback(async () => {
    try {
      setData(await adminService.securityEvents());
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (action, message) => {
    try {
      await action();
      toast.success(message);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (!data) return null;
  const blockedSet = new Set((data.blocked_ips || []).map((b) => b.ip));

  const submitManual = async (e) => {
    e.preventDefault();
    const ip = manual.ip.trim();
    if (!ip) return;
    await run(async () => {
      await adminService.blockIp(ip, manual.note.trim() || "Bloqueio manual");
      setManual({ ip: "", note: "" });
    }, `IP ${ip} bloqueado`);
  };

  const clearHistory = async (days) => {
    const label = days ? `com mais de ${days} dias` : "todo o histórico";
    if (!window.confirm(`Limpar ${label} de eventos de segurança?`)) return;
    await run(async () => {
      const r = await adminService.clearSecurityEvents(days);
      toast.message(`${r.deleted} evento(s) removido(s)`);
    }, "Histórico atualizado");
  };

  const tabBtn = (key, label, count) => (
    <button data-testid={`security-tab-${key}`} onClick={() => setTab(key)}
            className={`rounded-md px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${
              tab === key ? "bg-[#2563EB] text-white" : "text-[#94A3B8] hover:text-white"}`}>
      {label} <span className="font-mono">({count})</span>
    </button>
  );

  return (
    <div data-testid="security-events-panel"
         className="rounded-md border border-[#22252F] bg-[#12141A] p-5 space-y-5">
      <p className="flex items-center gap-2 text-sm text-white">
        <Activity className="h-4 w-4 text-[#60A5FA]" /> Proteção contra abuso
      </p>
      <p className="text-sm text-[#94A3B8]">
        Limites por IP: <span className="font-mono text-white">{data.limits.general_per_minute}</span> pedidos/min
        gerais · <span className="font-mono text-white">{data.limits.auth_per_minute}</span> pedidos/min em
        autenticação. Excedentes recebem <span className="font-mono">429</span>; IPs na lista negra recebem{" "}
        <span className="font-mono">403</span>. Aceita IP único ou intervalo CIDR
        (<span className="font-mono">203.0.113.0/24</span> bloqueia 256 endereços). O seu próprio IP
        não pode ser bloqueado.
      </p>

      <SecurityTotals totals={data.totals} />

      <div className="flex flex-wrap items-center gap-2 border-b border-[#22252F] pb-2">
        {tabBtn("events", "Eventos", data.events.length)}
        {tabBtn("blocked", "IPs bloqueados", blockedSet.size)}
      </div>

      {tab === "events" ? (
        <SecurityEventsTab
          events={data.events}
          blockedSet={blockedSet}
          onClear={clearHistory}
          onBlock={(ip) => run(
            () => adminService.blockIp(ip, "Bloqueado a partir dos eventos"),
            `IP ${ip} bloqueado`)}
        />
      ) : (
        <BlockedIpsTab
          blocked={data.blocked_ips || []}
          manual={manual}
          onManualChange={setManual}
          onSubmit={submitManual}
          onUnblock={(ip) => run(() => adminService.unblockIp(ip), `IP ${ip} desbloqueado`)}
        />
      )}
    </div>
  );
};
